import { Injectable, inject } from '@angular/core';
import * as joint from '@clientio/rappid';
import { Observable, debounceTime } from 'rxjs';

import { Guideline } from '@treetop/shared/model';

import { LinkAnchorData } from './operations.service';
import { PortsService } from './ports.service';
import { PositionService } from './position.service';
import { FlowchartConfig, FontAttributes } from '../base/flowchart.config';
import { BaseGraph } from '../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../flowchart/model.service';
import { BasePaper } from '../flowchart/paper/BasePaper';
import { FlowchartViewService } from '../flowchart/view.service';
import { HeaderElement } from '../shapes/shared-header';

@Injectable()
class SubflowService {
  private flowchartViewService = inject(FlowchartViewService);
  private flowchartModelService = inject(FlowchartModelService);
  private positionService = inject(PositionService);

  paperSource: BasePaper;
  graphSource: BaseGraph;
  guideline: Guideline;

  constructor() {
    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();
  }

  setGuideline(guideline: Guideline) {
    this.guideline = guideline;
  }

  createSubflowInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition: joint.g.PlainPoint,
  ): joint.dia.Cell {
    if (overwrittenPosition) {
      return this.createSubflow(overwrittenPosition.x, overwrittenPosition.y);
    }

    const subflowPosition = this.positionService.calculateStepPosition(
      sourceElement?.model.attributes.size,
      sourceElement?.model.attributes.position,
      sourceAnchor,
      FlowchartConfig.containerNode.size,
    );

    return this.createSubflow(subflowPosition.x, subflowPosition.y);
  }

  createSubflow(x: number, y: number, id?: string, uniqueId?: string): joint.dia.Cell {
    const subflow = new joint.shapes.standard.Rectangle({
      position: { x, y },
      size: FlowchartConfig.containerNode.size,
      z: 1,
      attrs: {
        body: {
          class: 'element--subflow__body',
          rx: FlowchartConfig.containerNode.styles.borderRadius,
          ry: FlowchartConfig.containerNode.styles.borderRadius,
          strokeDasharray: '0 4 0',
          uniqueId: uniqueId ? uniqueId : '',
          stroke: '#d0dce7',
        },
        label: null,
        text: { display: 'none' },
      },
    });

    if (id) {
      subflow.set('id', id);
    }

    subflow.attr('body/unique-id', uniqueId ?? subflow.id);

    subflow.set('isSubflow', true);
    PortsService.addPorts(subflow);
    let previousPosition = { x: x, y: y };

    subflow.on('change:position', (element) => {
      const currentPosition = element.attributes.position;
      const adjusteLinkId = [];
      if (
        element.attributes.allowSubflowManualLinkHandling &&
        (currentPosition.x !== previousPosition?.x || (currentPosition.y !== previousPosition?.y && previousPosition))
      ) {
        const embededElements = subflow.getEmbeddedCells();
        embededElements.forEach((cell) => {
          const links = this.graphSource.graph.getConnectedLinks(cell);
          const manualLinks = links.filter((link) => link.attributes.manual);
          const positionDifference = { x: currentPosition.x - previousPosition.x, y: currentPosition.y - previousPosition.y };

          if (manualLinks?.length > 0) {
            manualLinks.forEach((link) => {
              if (!adjusteLinkId.includes(link.id)) {
                const oldVertices = link.vertices();
                adjusteLinkId.push(link.id);
                const newVertices = oldVertices.map((v) => {
                  return { x: v.x + positionDifference.x, y: v.y + positionDifference.y };
                });
                link.vertices(newVertices);
              }
            });
          }
        });
      }
      previousPosition = element.attributes.position;
      // Set on FlowchartEventsService to allow/prevent manual link handling of subflow.
      element.set('allowSubflowManualLinkHandling', true);
    });

    const debouncedSizeObservable = new Observable((observer) => {
      subflow.on('change:size', (cellView: joint.dia.Element) => {
        observer.next(cellView);
      });
    });

    debouncedSizeObservable.pipe(debounceTime(300)).subscribe((cell: joint.dia.Element) => {
      this.keepLinksInSubflow(subflow, cell);
    });

    return subflow;
  }

  createSubflowHeader(text: string, x: number, y: number, id?: string): joint.dia.Cell {
    const subflowHeader = new HeaderElement(
      {
        position: {
          x: x + FlowchartConfig.containerNode.styles.headerNegativePadding,
          y: y + FlowchartConfig.containerNode.styles.headerNegativePadding,
        },
        size: FlowchartConfig.containerNode.headerSize,
        attrs: {
          body: {
            class: 'element--subflow__header__body',
            rx: FlowchartConfig.containerNode.styles.headerBorderRadius,
            ry: FlowchartConfig.containerNode.styles.headerBorderRadius,
            stroke: '#d0dce7',
          },
          label: {
            class: 'element--subflow__header__text',
            ...FontAttributes,
            fontSize: FontAttributes.fontSize,
            text,
            textVerticalAnchor: 'bottom',
            transform: 'translate(0px, 8px)',
            fill: '#063d70',
          },
        },
      },
      this.paperSource.paper,
    );

    if (id) {
      subflowHeader.set('id', id);
    }

    return subflowHeader;
  }

  addElementToSubflow(subflowId: string, element: joint.dia.Cell) {
    const subflow = this.graphSource.graph.getCell(subflowId) as joint.dia.Element;
    element.set('subflowParentId', subflowId);
    subflow.embed(element);
    this.graphSource.graph.addCell(element);
  }

  private keepLinksInSubflow(subflow: joint.shapes.standard.Rectangle, cell: joint.dia.Cell) {
    const links: joint.dia.Link[][] = (cell.attributes.embeds as string[]).map((el: string) =>
      this.graphSource.graph.getConnectedLinks(this.graphSource.graph.getCell(el)),
    );
    let flatLinks = links.flat();
    flatLinks = flatLinks.filter((item, abc) => {
      return abc === flatLinks.findIndex((obj) => obj.id === item.id);
    });

    // Functionality to put links inside
    const bBox = subflow.getBBox();
    const minX = bBox.x;
    const maxX = bBox.x + bBox.width;
    const minY = bBox.y;
    const maxY = bBox.y + bBox.height;

    flatLinks.forEach((link) => {
      link.vertices().forEach((vertex, index) => {
        let x = vertex.x;
        let y = vertex.y;
        if (vertex.x < minX) {
          x = minX + FlowchartConfig.paper.gridSize;
          link.attributes.manual = false;
        } else if (vertex.x > maxX) {
          link.attributes.manual = false;
          x = maxX - FlowchartConfig.paper.gridSize;
        }

        if (vertex.y < minY) {
          link.attributes.manual = false;
          y = minY + FlowchartConfig.paper.gridSize;
        } else if (vertex.y > maxY) {
          link.attributes.manual = false;
          y = maxY - FlowchartConfig.paper.gridSize;
        }

        link.vertex(index, { x, y });
      });
    });
  }
}

export default SubflowService;
